/**
 * HVP UI Manager - progress tracking
 *
 * Counts completed dropdowns and dispatches hvp_progress_updated on window.
 *
 * Split out of ui-manager.js (350-line rule): the methods below moved
 * VERBATIM and are installed on HVPUIManager.prototype by
 * ui-manager.js, so `this` is the same instance and behavior is identical.
 *
 * @package HaruVariationsPro
 * @version 1.0.0
 */

export const UiProgressMethods = {
    /**
     * Update progress indicator
     *
     * Counts completed dropdowns and calculates percentage.
     *
     * @param {Map|NodeList|Array} dropdowns - Dropdowns to check
     * @returns {Object} Progress object {completed, total, percentage}
     */
    updateProgressIndicator(dropdowns) {
        let selectElements;

        // Handle Map from controller or NodeList/Array
        if (dropdowns instanceof Map) {
            selectElements = Array.from(dropdowns.values());
        } else if (dropdowns) {
            selectElements = Array.from(dropdowns);
        } else {
            selectElements = Array.from(
                document.querySelectorAll('.variations_form select[name^="attribute_"]')
            );
        }

        const total = selectElements.length;
        let completed = 0;

        selectElements.forEach(select => {
            const value = select.value;
            const isSingleOption = select.classList.contains('hvp-single-option');

            // Count as completed if has value or is single-option (auto-selected)
            if ((value && value !== '') || isSingleOption) {
                completed++;
            }
        });

        const percentage = total > 0 ? Math.round((completed / total) * 100) : 0;

        // Update internal state
        this.progress = { completed, total, percentage };
        this.stats.progressUpdates++;

        this.log(`[Progress] ${completed}/${total} (${percentage}%)`);

        return this.progress;
    },

    /**
     * Emit progress update event
     *
     * Dispatches a custom event with current progress information.
     */
    emitProgressUpdate() {
        const progress = this.updateProgressIndicator();

        // Dispatch custom event
        const event = new CustomEvent('hvp_progress_updated', {
            bubbles: true,
            detail: {
                completed: progress.completed,
                total: progress.total,
                percentage: progress.percentage,
                timestamp: Date.now()
            }
        });

        window.dispatchEvent(event);
    },

    /**
     * Get current progress
     *
     * @returns {Object} Progress object {completed, total, percentage}
     */
    getProgress() {
        return { ...this.progress };
    },

    /**
     * Check if all selections are complete
     *
     * @returns {boolean}
     */
    isComplete() {
        return this.progress.percentage === 100;
    },
};

export default UiProgressMethods;
